import { useEffect, useRef } from "react";

/**
 * useFadeUp — attaches a one-time IntersectionObserver that adds the
 * `in` class to each child element with the `sm-fade` class as it
 * scrolls into view. Accepts a dependency key so it re-attaches when
 * async content loads into the subtree.
 */
export const useFadeUp = (depKey) => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof window === "undefined") return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const targets = node.querySelectorAll(".sm-fade:not(.in)");
    if (!targets.length) return undefined;
    if (reduced) {
      targets.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );
    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [depKey]);
  return ref;
};
